import type {
  ModelConstraint,
  OrderLimitConstraint,
} from '@/types/api/constraints'

import { OrderLimitConstraintType } from '@moment/horizon-data-access/proposal'
import isNil from 'lodash/isNil'

import { Priority } from '@/portfolio/proposal-builder/types'

export const MAX_SELLS_CONSTRAINT_ID = 'builder_max_sells'

function isSellsCapConstraint(
  constraint: ModelConstraint,
): constraint is OrderLimitConstraint {
  return (
    constraint.type === OrderLimitConstraintType.order_limit &&
    !isNil(constraint.max_sell_orders)
  )
}

function findMaxSellsConstraint(
  constraints: ModelConstraint[],
): OrderLimitConstraint | undefined {
  const matches = constraints.filter(isSellsCapConstraint)
  if (matches.length > 1) {
    throw new Error(
      `Expected at most one sells cap constraint, found ${matches.length}`,
    )
  }
  return matches[0]
}

function buildMaxSellsConstraint(maxSells: number): OrderLimitConstraint {
  return {
    id: MAX_SELLS_CONSTRAINT_ID,
    type: OrderLimitConstraintType.order_limit,
    max_sell_orders: maxSells,
    constraint_priority: Priority.High,
  }
}

export function readMaxSells(constraints: ModelConstraint[]): number | null {
  return findMaxSellsConstraint(constraints)?.max_sell_orders ?? null
}

export function applyMaxSells(
  constraints: ModelConstraint[],
  maxSells: number | null,
): ModelConstraint[] {
  const target = findMaxSellsConstraint(constraints)
  const removeCap = maxSells === null || !(maxSells > 0)

  if (removeCap) {
    return constraints.filter((constraint) => constraint !== target)
  }

  if (!target) {
    return [...constraints, buildMaxSellsConstraint(maxSells)]
  }

  return constraints.map((constraint) =>
    constraint === target
      ? {
          ...target,
          max_sell_orders: maxSells,
          constraint_priority: target.constraint_priority ?? Priority.High,
        }
      : constraint,
  )
}
